/**
 * MOCK fixture for the customer-facing Invasive Aedes aggregate report.
 *
 * Everything here is fabricated sample data for a Placer MVCD demonstration
 * page. There is no API, database, or backend behind any of it — the view
 * (`ts/view/InvasiveAedes.vue`) reads this module and nothing else.
 *
 * Geography: Placer County, California (Roseville, Rocklin, Lincoln, Auburn,
 * Granite Bay, Loomis, Antelope, Citrus Heights, Sheridan). Coordinates sit
 * inside the district's roughly 38.70–39.00 N / -121.36–-121.07 W footprint.
 *
 * Vocabulary (container types, larvae stages, find dates) follows the Placer
 * PIP / MapVision invasive-Aedes inspection form:
 * `doc/design/integrations/mapvision/pip-form-field-map.md`.
 */

import type { FeatureCollection, Polygon } from "geojson";

/** Developmental stages recorded at detection, in development order. */
export const LARVAE_STAGES = [
	"1st instar",
	"2nd instar",
	"3rd instar",
	"4th instar",
	"Pupae",
] as const;

export type LarvaeStage = (typeof LARVAE_STAGES)[number];

/**
 * Fixed larvae-stage palette, reused by the map markers, the site list and the
 * map legend so a stage reads the same colour everywhere on the page.
 * Bootstrap theme colours plus purple, one distinct hue per stage.
 */
export const LARVAE_STAGE_COLORS: Record<LarvaeStage, string> = {
	"1st instar": "#0dcaf0",
	"2nd instar": "#198754",
	"3rd instar": "#ffc107",
	"4th instar": "#dc3545",
	Pupae: "#6f42c1",
};

/** Container classes the district inspects (PIP form "Container type"). */
export const CONTAINER_TYPES = [
	"Tires",
	"Buckets & drums",
	"Catch basins",
	"Plant saucers",
	"Tarps & covers",
	"Discarded containers",
] as const;

export type ContainerType = (typeof CONTAINER_TYPES)[number];

/** bootstrap-icons glyph per container class, used as the site-list icon. */
export const CONTAINER_ICONS: Record<ContainerType, string> = {
	Tires: "bi-record-circle",
	"Buckets & drums": "bi-bucket",
	"Catch basins": "bi-funnel",
	"Plant saucers": "bi-flower2",
	"Tarps & covers": "bi-layers",
	"Discarded containers": "bi-box-seam",
};

/** A site where invasive Aedes larvae were found. */
export interface InvasiveAedesSite {
	id: string;
	name: string;
	latitude: number;
	longitude: number;
	container: ContainerType;
	/** ISO dates (YYYY-MM-DD) of every IA larval finding at the site. */
	findDates: string[];
	/** Stages observed across those findings. */
	stages: LarvaeStage[];
	/** Larval findings at the site; always `findDates.length`. */
	count: number;
}

/** An adult trap with a monthly count series for the season. */
export interface InvasiveAedesTrap {
	id: string;
	name: string;
	latitude: number;
	longitude: number;
	/** Counts aligned to `MONTH_LABELS`, one per month of the season year. */
	counts: number[];
}

export const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
] as const;

export const REPORT_PERIOD = "2026 season";

export const SITES: InvasiveAedesSite[] = [
	// Roseville
	{
		id: "IA-101",
		name: "Foothills Blvd & Junction Blvd",
		latitude: 38.7562,
		longitude: -121.2984,
		container: "Tires",
		findDates: ["2026-06-09", "2026-07-14"],
		stages: ["1st instar", "2nd instar"],
		count: 2,
	},
	{
		id: "IA-102",
		name: "Cirby Way at Riverside Ave",
		latitude: 38.7391,
		longitude: -121.2761,
		container: "Buckets & drums",
		findDates: ["2026-07-21"],
		stages: ["3rd instar"],
		count: 1,
	},
	{
		id: "IA-103",
		name: "Pleasant Grove Blvd & Woodcreek",
		latitude: 38.7823,
		longitude: -121.3049,
		container: "Catch basins",
		findDates: ["2026-05-19", "2026-06-23", "2026-08-04"],
		stages: ["3rd instar", "4th instar"],
		count: 3,
	},
	{
		id: "IA-104",
		name: "Highland Reserve",
		latitude: 38.7702,
		longitude: -121.3401,
		container: "Plant saucers",
		findDates: ["2026-08-11"],
		stages: ["2nd instar"],
		count: 1,
	},
	// Antelope / Citrus Heights
	{
		id: "IA-105",
		name: "Elverta Rd & Walerga Rd",
		latitude: 38.7124,
		longitude: -121.3339,
		container: "Discarded containers",
		findDates: ["2026-06-30"],
		stages: ["1st instar"],
		count: 1,
	},
	{
		id: "IA-106",
		name: "Auburn Blvd at Twin Oaks",
		latitude: 38.7043,
		longitude: -121.2782,
		container: "Tarps & covers",
		findDates: ["2026-07-07", "2026-08-18"],
		stages: ["3rd instar", "Pupae"],
		count: 2,
	},
	{
		id: "IA-107",
		name: "Roseville Rd & Winding Way",
		latitude: 38.7031,
		longitude: -121.3542,
		container: "Tires",
		findDates: ["2026-05-26", "2026-07-28", "2026-09-01"],
		stages: ["4th instar"],
		count: 3,
	},
	// Lincoln
	{
		id: "IA-108",
		name: "Ferrari Ranch Rd & Sterling Pkwy",
		latitude: 38.8861,
		longitude: -121.3053,
		container: "Buckets & drums",
		findDates: ["2026-06-16", "2026-08-25"],
		stages: ["2nd instar", "3rd instar"],
		count: 2,
	},
	{
		id: "IA-109",
		name: "Twelve Bridges Dr",
		latitude: 38.8689,
		longitude: -121.2856,
		container: "Plant saucers",
		findDates: ["2026-08-08"],
		stages: ["Pupae"],
		count: 1,
	},
	{
		id: "IA-110",
		name: "Hwy 65 & Nelson Ln",
		latitude: 38.8391,
		longitude: -121.2945,
		container: "Catch basins",
		findDates: ["2026-05-12", "2026-07-02"],
		stages: ["1st instar", "2nd instar"],
		count: 2,
	},
	// Auburn
	{
		id: "IA-111",
		name: "Nevada St & High St",
		latitude: 38.8996,
		longitude: -121.0741,
		container: "Tires",
		findDates: ["2026-06-04", "2026-07-16", "2026-09-08"],
		stages: ["3rd instar", "4th instar"],
		count: 3,
	},
	{
		id: "IA-112",
		name: "Bell Rd at Bowman",
		latitude: 38.9136,
		longitude: -121.0903,
		container: "Discarded containers",
		findDates: ["2026-07-30"],
		stages: ["4th instar"],
		count: 1,
	},
	{
		id: "IA-113",
		name: "Atwood Rd",
		latitude: 38.8763,
		longitude: -121.1188,
		container: "Tarps & covers",
		findDates: ["2026-06-11", "2026-08-13"],
		stages: ["1st instar"],
		count: 2,
	},
	// Granite Bay / Loomis
	{
		id: "IA-114",
		name: "Douglas Blvd & Auburn Folsom",
		latitude: 38.7466,
		longitude: -121.1654,
		container: "Plant saucers",
		findDates: ["2026-06-25", "2026-08-06"],
		stages: ["2nd instar", "3rd instar"],
		count: 2,
	},
	{
		id: "IA-115",
		name: "Taylor Rd at Horseshoe Bar",
		latitude: 38.8216,
		longitude: -121.1925,
		container: "Buckets & drums",
		findDates: ["2026-07-23"],
		stages: ["Pupae"],
		count: 1,
	},
	{
		id: "IA-116",
		name: "Sierra College Blvd & Douglas",
		latitude: 38.7625,
		longitude: -121.1971,
		container: "Catch basins",
		findDates: ["2026-05-05", "2026-06-18", "2026-08-20"],
		stages: ["3rd instar"],
		count: 3,
	},
	// Sheridan
	{
		id: "IA-117",
		name: "Riosa Rd near Sheridan",
		latitude: 38.9769,
		longitude: -121.3577,
		container: "Tires",
		findDates: ["2026-06-02", "2026-07-15"],
		stages: ["2nd instar"],
		count: 2,
	},
];

export const TRAPS: InvasiveAedesTrap[] = [
	{
		id: "TR-12",
		name: "Foothills Blvd Trap 12",
		latitude: 38.756,
		longitude: -121.298,
		counts: [0, 0, 0, 2, 5, 11, 22, 27, 18, 8, 2, 0],
	},
	{
		id: "TR-07",
		name: "Cirby Way Trap 07",
		latitude: 38.7395,
		longitude: -121.2765,
		counts: [0, 0, 1, 3, 7, 14, 26, 31, 21, 9, 3, 1],
	},
	{
		id: "TR-03",
		name: "Elverta Rd Trap 03",
		latitude: 38.713,
		longitude: -121.3345,
		counts: [0, 0, 0, 1, 3, 8, 15, 19, 12, 5, 1, 0],
	},
	{
		id: "TR-21",
		name: "Auburn Blvd Trap 21",
		latitude: 38.704,
		longitude: -121.279,
		counts: [0, 0, 0, 2, 4, 9, 17, 20, 14, 6, 2, 0],
	},
	{
		id: "TR-09",
		name: "Ferrari Ranch Trap 09",
		latitude: 38.8865,
		longitude: -121.305,
		counts: [0, 0, 0, 1, 4, 10, 20, 24, 15, 6, 1, 0],
	},
	{
		id: "TR-14",
		name: "Twelve Bridges Trap 14",
		latitude: 38.8692,
		longitude: -121.286,
		counts: [0, 0, 0, 0, 2, 6, 12, 16, 10, 4, 1, 0],
	},
	{
		id: "TR-18",
		name: "Nevada St Trap 18",
		latitude: 38.9,
		longitude: -121.0745,
		counts: [0, 0, 0, 1, 3, 7, 13, 17, 11, 5, 1, 0],
	},
	{
		id: "TR-05",
		name: "Douglas Blvd Trap 05",
		latitude: 38.7468,
		longitude: -121.166,
		counts: [0, 0, 0, 2, 6, 12, 23, 28, 19, 8, 2, 0],
	},
	{
		id: "TR-11",
		name: "Taylor Rd Trap 11",
		latitude: 38.822,
		longitude: -121.193,
		counts: [0, 0, 0, 1, 4, 9, 16, 21, 13, 6, 2, 0],
	},
	{
		id: "TR-02",
		name: "Riosa Rd Trap 02",
		latitude: 38.9772,
		longitude: -121.358,
		counts: [0, 0, 0, 1, 3, 7, 14, 18, 11, 5, 1, 0],
	},
];

/** Larval findings across every site; hex counts below sum to this. */
export const TOTAL_FINDINGS = SITES.reduce((sum, s) => sum + s.count, 0);

/** Opening "current location" so the trap chart is populated on load. */
export const DEFAULT_LOCATION = { latitude: 38.7521, longitude: -121.288 };

export const DEFAULT_LOCATION_LABEL = "Roseville (district centre)";

// ---------------------------------------------------------------------------
// H3-style hex overlay
// ---------------------------------------------------------------------------

export interface InvasiveAedesHexProperties {
	/** Hex resolution: 7 = district-wide cluster, 9 = per-site. */
	resolution: number;
	/** IA larval findings inside the cell. */
	count: number;
}

export type InvasiveAedesHexCollection = FeatureCollection<
	Polygon,
	InvasiveAedesHexProperties
>;

export const HEX_RESOLUTIONS = [7, 8, 9] as const;

/**
 * Approximate H3 average edge length, expressed in degrees of latitude, for
 * the three resolutions we render. There is no h3 dependency in this repo, so
 * the hexes are generated as regular polygons at roughly the right size — the
 * mock only needs the *visual* effect of a hex aggregation layer.
 */
const HEX_EDGE_DEGREES: Record<number, number> = {
	7: 0.0126,
	8: 0.0048,
	9: 0.0018,
};

/** A flat-top regular hexagon of circumradius `edgeDegrees` around a point. */
function hexRing(
	latitude: number,
	longitude: number,
	edgeDegrees: number,
): [number, number][] {
	const lonScale = 1 / Math.cos((latitude * Math.PI) / 180);
	const ring: [number, number][] = [];
	for (let i = 0; i < 6; i++) {
		const angle = (Math.PI / 3) * i;
		ring.push([
			longitude + edgeDegrees * lonScale * Math.cos(angle),
			latitude + edgeDegrees * Math.sin(angle),
		]);
	}
	ring.push(ring[0]);
	return ring;
}

function hexFeature(
	latitude: number,
	longitude: number,
	resolution: number,
	count: number,
): InvasiveAedesHexCollection["features"][number] {
	return {
		type: "Feature",
		properties: { resolution, count },
		geometry: {
			type: "Polygon",
			coordinates: [hexRing(latitude, longitude, HEX_EDGE_DEGREES[resolution])],
		},
	};
}

/**
 * Spatial clusters. Resolution-7 hexes are drawn at these centroids with the
 * summed findings of their member sites; resolution 8 and 9 hexes are drawn
 * per site. Counts therefore stay consistent with `SITES`.
 */
export const HEX_CLUSTERS = [
	{
		name: "Roseville",
		latitude: 38.762,
		longitude: -121.305,
		siteIds: ["IA-101", "IA-102", "IA-103", "IA-104"],
	},
	{
		name: "Antelope / Citrus Heights",
		latitude: 38.7065,
		longitude: -121.3221,
		siteIds: ["IA-105", "IA-106", "IA-107"],
	},
	{
		name: "Lincoln",
		latitude: 38.8647,
		longitude: -121.2951,
		siteIds: ["IA-108", "IA-109", "IA-110"],
	},
	{
		name: "Auburn",
		latitude: 38.8965,
		longitude: -121.0944,
		siteIds: ["IA-111", "IA-112", "IA-113"],
	},
	{
		name: "Granite Bay / Loomis",
		latitude: 38.7769,
		longitude: -121.185,
		siteIds: ["IA-114", "IA-115", "IA-116"],
	},
	{
		name: "Sheridan",
		latitude: 38.9769,
		longitude: -121.3577,
		siteIds: ["IA-117"],
	},
] as const;

export const H3_CELLS: InvasiveAedesHexCollection = {
	type: "FeatureCollection",
	features: [
		// Resolution 7 — one aggregate hex per cluster.
		...HEX_CLUSTERS.map((cluster) =>
			hexFeature(
				cluster.latitude,
				cluster.longitude,
				7,
				cluster.siteIds.reduce(
					(sum, id) => sum + (SITES.find((site) => site.id === id)?.count ?? 0),
					0,
				),
			),
		),
		// Resolution 8 and 9 — one hex per found site.
		...HEX_RESOLUTIONS.slice(1).flatMap((resolution) =>
			SITES.map((site) =>
				hexFeature(site.latitude, site.longitude, resolution, site.count),
			),
		),
	],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Great-circle distance in kilometres. */
export function distanceKm(
	aLatitude: number,
	aLongitude: number,
	bLatitude: number,
	bLongitude: number,
): number {
	const toRad = (deg: number) => (deg * Math.PI) / 180;
	const dLat = toRad(bLatitude - aLatitude);
	const dLon = toRad(bLongitude - aLongitude);
	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(aLatitude)) *
			Math.cos(toRad(bLatitude)) *
			Math.sin(dLon / 2) ** 2;
	return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** The `limit` traps closest to a point, nearest first. */
export function nearestTraps(
	latitude: number,
	longitude: number,
	limit = 4,
): InvasiveAedesTrap[] {
	return [...TRAPS]
		.sort(
			(a, b) =>
				distanceKm(latitude, longitude, a.latitude, a.longitude) -
				distanceKm(latitude, longitude, b.latitude, b.longitude),
		)
		.slice(0, limit);
}

export interface ContainerTypeRollup {
	container: ContainerType;
	sites: number;
	findings: number;
}

/** Site/finding counts per container class, in `CONTAINER_TYPES` order. */
export function containerTypeRollup(): ContainerTypeRollup[] {
	return CONTAINER_TYPES.filter((c) =>
		SITES.some((s) => s.container === c),
	).map((container) => {
		const matching = SITES.filter((s) => s.container === container);
		return {
			container,
			sites: matching.length,
			findings: matching.reduce((sum, s) => sum + s.count, 0),
		};
	});
}
